import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useRef
} from "react";

import { Driver, driver } from "driver.js";
import "driver.js/dist/driver.css";
import "./index.css";
import config from "config/config";
import { PropertyGroupStep } from "../types";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { propertyGroupTitleMap } from "../constants";

const driverObj = driver();

const getStepsObj = (
  currentStepId: PropertyGroupStep,
  propertiesDictionary: string
) =>
  driver({
    steps: [
      {
        element: `#${currentStepId}`,
        popover: {
          title: `First you can split ${propertiesDictionary} by ${propertyGroupTitleMap[currentStepId] || currentStepId} by clicking this gray column`,
          showButtons: ["next", "close"],
          showProgress: true
        },
        disableActiveInteraction: true
      },
      {
        element: "#footer-buttons-property-groups",
        popover: {
          title: "Once you’re finished, press Continue",
          showButtons: ["previous", "close", "next"],
          showProgress: true,
          nextBtnText: "Done"
        },
        disableActiveInteraction: true
      }
    ]
  });

const getInitDriverObj = (
  firstStep: PropertyGroupStep,
  propertiesDictionary: string
) =>
  driver({
    steps: [
      {
        element: `#${firstStep}`,
        popover: {
          title: `First you can split ${propertiesDictionary} by ${propertyGroupTitleMap[firstStep] || firstStep} by clicking this gray column`,
          showButtons: ["next", "close"],
          showProgress: true
        },
        disableActiveInteraction: true
      },
      {
        element: "#footer-buttons-property-groups",
        popover: {
          title: "Once you’re finished, press Continue",
          showButtons: ["previous", "close", "next"],
          nextBtnText: "Done",
          showProgress: true
        },
        disableActiveInteraction: true
      }
    ]
  });

const ctx = createContext<{
  driver: Driver;
  helpDriver?: Driver;
  destroyAllDrivers: () => void;
}>({
  driver: driverObj,
  destroyAllDrivers: () => {}
});

export const SpotlightProvider: FC<{
  children: ReactNode;
  currentStep: PropertyGroupStep;
  selectedSplitOptions: PropertyGroupStep[];
}> = ({ children, currentStep, selectedSplitOptions }) => {
  const initDriverShown = useRef(false);
  const initDriverRef = useRef<Driver>();
  const { properties } = useUnitsPropertiesDictionary();

  const helpDriver = getStepsObj(currentStep, properties);

  useEffect(() => {
    if (initDriverShown.current) return;

    const initShownDriverShow = window.localStorage.getItem(
      "property-groups-spotlight"
    );

    if (
      selectedSplitOptions[0] === currentStep &&
      initShownDriverShow !== "shown"
    )
      setTimeout(() => {
        const initDriver = getInitDriverObj(currentStep, properties);
        initDriverRef.current = initDriver;
        initDriver.drive();

        window.localStorage.setItem("property-groups-spotlight", "shown");
        initDriverShown.current = true;
      }, 200);
  }, [currentStep, selectedSplitOptions, properties]);

  const destroyAllDrivers = () => {
    driverObj.destroy();
    initDriverRef.current?.destroy();
    helpDriver.destroy();
  };

  return (
    <ctx.Provider
      value={{
        driver: driverObj,
        helpDriver,
        destroyAllDrivers
      }}
    >
      {children}
    </ctx.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSpotlightContext = () => {
  const context = useContext(ctx);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- possible when used outside of provider
  if (!context && config.environment !== "production")
    throw new Error(
      "useSpotlightContext must be used within a SpotlightProvider"
    );

  return context;
};
